import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

type ViewMode = 'full' | 'guest';

interface ViewModeContextType {
  viewMode: ViewMode;
  isGuestView: boolean;
  canToggleView: boolean;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};

interface ViewModeProviderProps {
  children: ReactNode;
}

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children }) => {
  const { user } = useAuth();

  // Determine if user can toggle (family members and admins)
  const canToggleView = Boolean(user?.isFamily || user?.isAdmin);

  // Real guests always see guest view
  const isRealGuest = Boolean(user && !user.isFamily && !user.isAdmin);

  // Initialize view mode based on user type
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    return isRealGuest ? 'guest' : 'full';
  });

  // Reset view mode when user changes
  useEffect(() => {
    if (isRealGuest) {
      setViewModeState('guest');
    } else if (user) {
      setViewModeState('full');
    }
  }, [user?.id, isRealGuest]);

  const toggleViewMode = () => {
    if (canToggleView) {
      setViewModeState((prev) => (prev === 'full' ? 'guest' : 'full'));
    }
  };

  const setViewMode = (mode: ViewMode) => {
    if (canToggleView || mode === 'guest') {
      setViewModeState(mode);
    }
  };

  const value: ViewModeContextType = {
    viewMode,
    isGuestView: viewMode === 'guest',
    canToggleView,
    setViewMode,
    toggleViewMode,
  };

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};
